import { Button, Modal, Textarea, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { HelpCircle, PlusIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type {
  LeaveRequestListItem,
  LeaveRequestStatus,
} from "@/modules/hr/leave/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import {
  useApproveLeaveRequestMutation,
  useBulkApproveLeaveRequestsMutation,
  useBulkDeleteLeaveRequestsMutation,
  useBulkRejectLeaveRequestsMutation,
  useDeleteLeaveRequestMutation,
  useLeaveRequestsQuery,
  useRejectLeaveRequestMutation,
} from "../api/queries";
import {
  LeaveApprovalListTour,
  useLeaveApprovalListTour,
} from "../tour/LeaveApprovalListTour";
import {
  LeaveApprovalFilter,
  type LeaveApprovalSearchParams,
} from "../ui/LeaveApprovalFilter";
import { LeaveApprovalListTable } from "../ui/LeaveApprovalListTable";
import {
  LeaveApproveAdjustFields,
  leaveApproveValuesFromRequest,
  type LeaveApproveAdjustValues,
} from "../ui/LeaveApproveAdjustFields";

export function LeaveApprovalsPage() {
  const nav = useNavigate({ from: "/app/hr/leave/approvals" });
  const { has } = usePermissions();
  const canApproveHr = has(Permissions.hr_leave.approve);
  const defaultQueueStatus: LeaveRequestStatus = canApproveHr
    ? "pending_hr"
    : "pending_line";
  const search = useSearch({
    from: "/app/hr/leave/approvals",
  }) as LeaveApprovalSearchParams & {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  };

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const filterByCreated = search.filterByCreated !== false;
  const periodYear = search.periodYear ?? currentYear;
  const periodMonth = search.periodMonth ?? currentMonth;
  const periodFromOnly = search.periodFromOnly !== false;

  const sortBy = search.sort?.[0]?.field ?? "startDate";
  const sortOrder = (search.sort?.[0]?.dir ?? "desc") as "asc" | "desc";

  const query = useMemo(
    () =>
      filterByCreated
        ? {
            offset: search.offset ?? 0,
            limit: search.limit ?? 20,
            sort: search.sort,
            createdYear: periodYear,
            createdMonth: periodMonth,
            createdFromOnly: periodFromOnly,
            status:
              (search.status as LeaveRequestStatus | undefined) ??
              defaultQueueStatus,
            search: search.search ?? undefined,
          }
        : {
            offset: search.offset ?? 0,
            limit: search.limit ?? 20,
            sort: search.sort,
            periodYear,
            periodMonth,
            periodFromOnly,
            status:
              (search.status as LeaveRequestStatus | undefined) ??
              defaultQueueStatus,
            search: search.search ?? undefined,
          },
    [
      search.offset,
      search.limit,
      search.sort,
      search.status,
      search.search,
      filterByCreated,
      periodYear,
      periodMonth,
      periodFromOnly,
      defaultQueueStatus,
    ],
  );

  const listQuery = useLeaveRequestsQuery(query);
  const data = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;
  const limit = meta?.limit ?? 20;
  const offset = search.offset ?? 0;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const approveMutation = useApproveLeaveRequestMutation();
  const rejectMutation = useRejectLeaveRequestMutation();
  const deleteMutation = useDeleteLeaveRequestMutation();
  const bulkApproveMutation = useBulkApproveLeaveRequestsMutation();
  const bulkRejectMutation = useBulkRejectLeaveRequestsMutation();
  const bulkDeleteMutation = useBulkDeleteLeaveRequestsMutation();

  const [approveModal, setApproveModal] = useState<LeaveRequestListItem | null>(
    null,
  );
  const [approveAdjust, setApproveAdjust] = useState<LeaveApproveAdjustValues | null>(
    null,
  );
  const [rejectModal, setRejectModal] = useState<LeaveRequestListItem | null>(
    null,
  );
  const [rejectReason, setRejectReason] = useState("");
  const [deleteModal, setDeleteModal] = useState<LeaveRequestListItem | null>(
    null,
  );
  const [bulkApproveIds, setBulkApproveIds] = useState<string[] | null>(null);
  const [bulkRejectIds, setBulkRejectIds] = useState<string[] | null>(null);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const [bulkDeleteIds, setBulkDeleteIds] = useState<string[] | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const searchForNav = useMemo(
    () => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      sort: search.sort,
      periodYear: search.periodYear ?? currentYear,
      periodMonth: search.periodMonth ?? currentMonth,
      periodFromOnly: search.periodFromOnly !== false,
      filterByCreated: search.filterByCreated !== false,
      status: search.status,
      search: search.search,
    }),
    [search],
  );

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        periodYear: search.periodYear ?? currentYear,
        periodMonth: search.periodMonth ?? currentMonth,
        periodFromOnly: search.periodFromOnly !== false,
        filterByCreated: search.filterByCreated !== false,
        status: search.status,
        search: search.search,
      },
    });
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
          periodYear: search.periodYear ?? currentYear,
          periodMonth: search.periodMonth ?? currentMonth,
          periodFromOnly: search.periodFromOnly !== false,
          filterByCreated: search.filterByCreated !== false,
          status: search.status,
          search: search.search,
        },
      });
    },
    [
      nav,
      search.limit,
      search.periodYear,
      search.periodMonth,
      search.periodFromOnly,
      search.filterByCreated,
      search.status,
      search.search,
    ],
  );

  const searchForDetail = useMemo(
    () => ({ ...searchForNav, from: "approvals" as const }),
    [searchForNav],
  );
  const handleViewDetail = useCallback(
    (row: LeaveRequestListItem) => {
      nav({
        to: "/app/hr/leave/requests/$id",
        params: { id: row.id },
        search: searchForDetail,
      });
    },
    [nav, searchForDetail],
  );

  const handleEdit = useCallback(
    (row: LeaveRequestListItem) => {
      nav({
        to: "/app/hr/leave/requests/$id/edit",
        params: { id: row.id },
        search: searchForDetail,
      });
    },
    [nav, searchForDetail],
  );

  const handleApproveClick = (row: LeaveRequestListItem) => {
    setApproveModal(row);
    setApproveAdjust(leaveApproveValuesFromRequest(row));
  };
  const handleRejectClick = (row: LeaveRequestListItem) => {
    setRejectModal(row);
    setRejectReason("");
  };
  const handleDeleteClick = (row: LeaveRequestListItem) => setDeleteModal(row);

  useEffect(() => {
    if (approveModal) {
      setApproveAdjust(leaveApproveValuesFromRequest(approveModal));
    } else {
      setApproveAdjust(null);
    }
  }, [approveModal]);

  const approveExceedsRemaining =
    approveModal?.remainingDays != null &&
    approveAdjust != null &&
    approveAdjust.days > approveModal.remainingDays;

  const handleApproveConfirm = useCallback(async () => {
    if (!approveModal || !approveAdjust) return;
    if (approveExceedsRemaining) {
      toast.error("ຈຳນວນວັນລາເກີນສິດຄົງເຫຼືອ");
      return;
    }
    try {
      await approveMutation.mutateAsync({
        id: approveModal.id,
        startDate: approveAdjust.startDate,
        endDate: approveAdjust.endDate,
        days: approveAdjust.days,
      });
      toast.success("ອະນຸມັດຄຳຂໍລາສຳເລັດ");
      setApproveModal(null);
      listQuery.refetch();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ");
    }
  }, [
    approveModal,
    approveAdjust,
    approveExceedsRemaining,
    approveMutation,
    listQuery,
  ]);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectModal) return;
    try {
      await rejectMutation.mutateAsync({
        id: rejectModal.id,
        rejectionReason: rejectReason.trim() || undefined,
      });
      toast.success("ປະຕິເສດຄຳຂໍລາສຳເລັດ");
      setRejectModal(null);
      setRejectReason("");
      listQuery.refetch();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ");
    }
  }, [rejectModal, rejectReason, rejectMutation, listQuery]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal) return;
    try {
      await deleteMutation.mutateAsync(deleteModal.id);
      toast.success("ລຶບຄຳຂໍລາສຳເລັດ");
      setDeleteModal(null);
      setSelectedIds((prev) => prev.filter((id) => id !== deleteModal.id));
      listQuery.refetch();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ");
    }
  }, [deleteModal, deleteMutation, listQuery]);

  const handleBulkApprove = (ids: string[]) => setBulkApproveIds(ids);
  const handleBulkReject = (ids: string[]) => {
    setBulkRejectReason("");
    setBulkRejectIds(ids);
  };
  const handleBulkDelete = (ids: string[]) => setBulkDeleteIds(ids);

  const handleBulkApproveConfirm = useCallback(async () => {
    if (!bulkApproveIds?.length) return;
    setBulkLoading(true);
    try {
      const { approved } =
        await bulkApproveMutation.mutateAsync(bulkApproveIds);
      toast.success(`ອະນຸມັດ ${approved} ຄຳຂໍລາສຳເລັດ`);
      setBulkApproveIds(null);
      setSelectedIds((prev) =>
        prev.filter((id) => !bulkApproveIds.includes(id)),
      );
      listQuery.refetch();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ");
    } finally {
      setBulkLoading(false);
    }
  }, [bulkApproveIds, bulkApproveMutation, listQuery]);

  const handleBulkRejectConfirm = useCallback(async () => {
    if (!bulkRejectIds?.length) return;
    const reason = bulkRejectReason.trim() || undefined;
    setBulkLoading(true);
    try {
      const { rejected } = await bulkRejectMutation.mutateAsync({
        ids: bulkRejectIds,
        rejectionReason: reason,
      });
      toast.success(`ປະຕິເສດ ${rejected} ຄຳຂໍລາສຳເລັດ`);
      setBulkRejectIds(null);
      setBulkRejectReason("");
      setSelectedIds((prev) =>
        prev.filter((id) => !bulkRejectIds.includes(id)),
      );
      listQuery.refetch();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ");
    } finally {
      setBulkLoading(false);
    }
  }, [bulkRejectIds, bulkRejectReason, bulkRejectMutation, listQuery]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    if (!bulkDeleteIds?.length) return;
    setBulkLoading(true);
    try {
      const { deleted } = await bulkDeleteMutation.mutateAsync(bulkDeleteIds);
      toast.success(`ລຶບ ${deleted} ຄຳຂໍລາສຳເລັດ`);
      setBulkDeleteIds(null);
      setSelectedIds((prev) =>
        prev.filter((id) => !bulkDeleteIds.includes(id)),
      );
      listQuery.refetch();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ");
    } finally {
      setBulkLoading(false);
    }
  }, [bulkDeleteIds, bulkDeleteMutation, listQuery]);

  const tour = useLeaveApprovalListTour();

  return (
    <>
      <Header />
      <Main>
        <LeaveApprovalListTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex items-center justify-between"
          data-tourid="leave-approval-toolbar"
        >
          <div className="flex items-center gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-2xl">ລາຍການອະນຸມັດລາ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                ເບິ່ງ ແລະ ອະນຸມັດ ຫຼື ປະຕິເສດຄຳຂໍລາພັກ
              </p>
            </div>
          </div>
          <div>
            <Button onClick={() => nav({ to: "/app/hr/leave/requests/new" })}>
              <PlusIcon className="h-4 w-4" />
              ສ້າງຄຳຂໍລາພັກ
            </Button>
          </div>
        </div>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="leave-approval-filter"
        >
          <LeaveApprovalFilter
            searchParams={{
              ...search,
              periodYear: search.periodYear ?? currentYear,
              periodMonth: search.periodMonth ?? currentMonth,
              periodFromOnly: search.periodFromOnly !== false,
              filterByCreated: search.filterByCreated !== false,
            }}
            fromRoute="/app/hr/leave/approvals"
            defaultPeriodFromOnly={true}
            defaultFilterByCreated={true}
            defaultStatus={defaultQueueStatus}
          />
          <div data-tourid="leave-approval-table">
            <LeaveApprovalListTable
              data={data}
              isLoading={listQuery.isLoading}
              offset={offset}
              limit={limit}
              totalCount={totalCount}
              onPaginationChange={handlePaginationChange}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortingChange={handleSortingChange}
              onApprove={handleApproveClick}
              onReject={handleRejectClick}
              onViewDetail={handleViewDetail}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              canApproveHr={canApproveHr}
              canBulkApprove={canApproveHr}
              showActions
              selectedIds={selectedIds}
              onSelectedIdsChange={setSelectedIds}
              onBulkApprove={handleBulkApprove}
              onBulkReject={handleBulkReject}
              onBulkDelete={handleBulkDelete}
            />
          </div>
        </div>

        <Modal
          open={!!approveModal}
          onOpenChange={(open) => !open && setApproveModal(null)}
          title="ຢືນຢັນອະນຸມັດຄຳຂໍລາ"
          description={
            approveModal
              ? `ອະນຸມັດຄຳຂໍລາຂອງ ${approveModal.employeeName} (${approveModal.leaveTypeName})`
              : ""
          }
          footer={
            <>
              <Button variant="outline" onClick={() => setApproveModal(null)}>
                ຍົກເລີກ
              </Button>
              <Button
                onClick={handleApproveConfirm}
                disabled={
                  approveMutation.isPending ||
                  approveExceedsRemaining ||
                  !approveAdjust
                }
              >
                ອະນຸມັດ
              </Button>
            </>
          }
        >
          {approveModal && approveAdjust ? (
            <LeaveApproveAdjustFields
              values={approveAdjust}
              onChange={setApproveAdjust}
              remainingDays={approveModal.remainingDays}
            />
          ) : null}
        </Modal>

        <Modal
          open={!!rejectModal}
          onOpenChange={(open) => {
            if (!open) {
              setRejectModal(null);
              setRejectReason("");
            }
          }}
          title="ປະຕິເສດຄຳຂໍລາ"
          description={
            rejectModal
              ? `ປະຕິເສດຄຳຂໍລາຂອງ ${rejectModal.employeeName} (${rejectModal.leaveTypeName})? ເຫດຜົນ (ບໍ່ບັງຄັບ):`
              : ""
          }
          footer={
            <>
              <Button variant="outline" onClick={() => setRejectModal(null)}>
                ຍົກເລີກ
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejectConfirm}
                disabled={rejectMutation.isPending}
              >
                ປະຕິເສດ
              </Button>
            </>
          }
        >
          {rejectModal && (
            <div className="py-2">
              <Textarea
                placeholder="ເຫດຜົນການປະຕິເສດ (optional)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="w-full"
              />
            </div>
          )}
        </Modal>

        <Modal
          open={!!deleteModal}
          onOpenChange={(open) => !open && setDeleteModal(null)}
          title="ຢືນຢັນລຶບຄຳຂໍລາ"
          description={
            deleteModal
              ? `ລຶບຄຳຂໍລາຂອງ ${deleteModal.employeeName} (${deleteModal.leaveTypeName})? ເຮັດໄດ້ແຕ່ສະຖານະລໍຖ້າອະນຸມັດເທົ່ານີ້.`
              : ""
          }
          footer={
            <>
              <Button variant="outline" onClick={() => setDeleteModal(null)}>
                ຍົກເລີກ
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleteMutation.isPending}
              >
                ລຶບ
              </Button>
            </>
          }
        />

        <Modal
          open={bulkApproveIds != null && bulkApproveIds.length > 0}
          onOpenChange={(open) => !open && setBulkApproveIds(null)}
          title="ອະນຸມັດຄຳຂໍລາ (ທີ່ເລືອກ)"
          description={`ອະນຸມັດ ${bulkApproveIds?.length ?? 0} ຄຳຂໍລາທີ່ເລືອກບໍ? (ລາຍການທີ່ລໍຖ້າອະນຸມັດເທົ່ານັ້ນຈະຖືກອະນຸມັດ)`}
          footer={
            <>
              <Button
                variant="outline"
                onClick={() => setBulkApproveIds(null)}
                disabled={bulkLoading}
              >
                ຍົກເລີກ
              </Button>
              <Button onClick={handleBulkApproveConfirm} disabled={bulkLoading}>
                {bulkLoading ? "ກຳລັງອະນຸມັດ..." : "ອະນຸມັດ"}
              </Button>
            </>
          }
        />

        <Modal
          open={bulkRejectIds != null && bulkRejectIds.length > 0}
          onOpenChange={(open) => {
            if (!open) {
              setBulkRejectIds(null);
              setBulkRejectReason("");
            }
          }}
          title="ປະຕິເສດຄຳຂໍລາ (ທີ່ເລືອກ)"
          description={`ປະຕິເສດ ${bulkRejectIds?.length ?? 0} ຄຳຂໍລາທີ່ເລືອກບໍ? ເຫດຜົນ (ບໍ່ບັງຄັບ):`}
          footer={
            <>
              <Button
                variant="outline"
                onClick={() => setBulkRejectIds(null)}
                disabled={bulkLoading}
              >
                ຍົກເລີກ
              </Button>
              <Button
                variant="destructive"
                onClick={handleBulkRejectConfirm}
                disabled={bulkLoading}
              >
                {bulkLoading ? "ກຳລັງປະຕິເສດ..." : "ປະຕິເສດ"}
              </Button>
            </>
          }
        >
          {bulkRejectIds != null && bulkRejectIds.length > 0 && (
            <div className="py-2">
              <Textarea
                placeholder="ເຫດຜົນການປະຕິເສດ (ບໍ່ບັງຄັບ)"
                value={bulkRejectReason}
                onChange={(e) => setBulkRejectReason(e.target.value)}
                rows={3}
                className="w-full"
              />
            </div>
          )}
        </Modal>

        <Modal
          open={bulkDeleteIds != null && bulkDeleteIds.length > 0}
          onOpenChange={(open) => !open && setBulkDeleteIds(null)}
          title="ລຶບຄຳຂໍລາ (ທີ່ເລືອກ)"
          description={`ລຶບ ${bulkDeleteIds?.length ?? 0} ຄຳຂໍລາທີ່ເລືອກບໍ? ເຮັດໄດ້ແຕ່ສະຖານະລໍຖ້າອະນຸມັດເທົ່ານີ້.`}
          footer={
            <>
              <Button
                variant="outline"
                onClick={() => setBulkDeleteIds(null)}
                disabled={bulkLoading}
              >
                ຍົກເລີກ
              </Button>
              <Button
                variant="destructive"
                onClick={handleBulkDeleteConfirm}
                disabled={bulkLoading}
              >
                {bulkLoading ? "ກຳລັງລຶບ..." : "ລຶບ"}
              </Button>
            </>
          }
        />
      </Main>
    </>
  );
}
